import { Button, Text, Flex, HStack, VStack } from '@chakra-ui/react';
import { ReactElement, useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ModalClassCreationVariantProps, SharedModalProps } from 'types/Modal';
import { MultiSelectField, RHFFormInput } from 'components/common/Form';
import { useUsers } from 'hooks/api/backend/managament/getB2BUsers';
import { useCreateClass } from 'hooks/api/backend/managament/getClasses';
import Joi from 'joi';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { SchoolUserRole } from '@novaheal/types';
import { EducationIcon, SchoolIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { Form } from 'components/common/Form/v2';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

const SEARCH_DEBOUNCE_MS = 300;

type Props = SharedModalProps & ModalClassCreationVariantProps;

type UserOption = {
  id: string;
  displayName: string;
  email: string;
};

type ClassCreationInput = {
  className: string;
  students: string[];
  teachers: string[];
  guides: string[];
};

const schema = Joi.object({
  className: Joi.string().trim().required().messages({
    'string.empty': 'Bitte gib einen Namen für den Kurs ein.',
    'string.trim': 'Bitte gib einen Namen für den Kurs ein.',
  }),
  teachers: Joi.array().items(Joi.string()).optional(),
  guides: Joi.array().items(Joi.string()).optional(),
  students: Joi.array().items(Joi.string()).optional(),
});

export const ClassCreationModal = ({ handleClose, isShown }: Props): ReactElement => {
  const [searchTermTeachers, setSearchTermTeachers] = useState('');
  const [debouncedSearchTermTeachers, setDebouncedSearchTermTeachers] = useState('');
  const [searchTermGuides, setSearchTermGuides] = useState('');
  const [debouncedSearchTermGuides, setDebouncedSearchTermGuides] = useState('');
  const [searchTermStudents, setSearchTermStudents] = useState('');
  const [debouncedSearchTermStudents, setDebouncedSearchTermStudents] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTermTeachers(searchTermTeachers);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTermTeachers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTermGuides(searchTermGuides);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTermGuides]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTermStudents(searchTermStudents);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTermStudents]);

  const queryClient = useQueryClient();
  const toast = useNovaToast();
  const { data: teachersData, refetch: refetchTeachers } = useUsers({
    page: 1,
    searchQuery: debouncedSearchTermTeachers || undefined,
    sortBy: 'firstName',
    sortOrder: 'asc',
    role: [SchoolUserRole.TEACHER, SchoolUserRole.ADMIN],
    all: !debouncedSearchTermTeachers,
    limit: debouncedSearchTermTeachers ? 50 : undefined,
  });

  const { data: guidesData, refetch: refetchGuides } = useUsers({
    page: 1,
    searchQuery: debouncedSearchTermGuides || undefined,
    sortBy: 'firstName',
    sortOrder: 'asc',
    role: [SchoolUserRole.GUIDE, SchoolUserRole.ADMIN],
    all: !debouncedSearchTermGuides,
    limit: debouncedSearchTermGuides ? 50 : undefined,
  });

  const { data: studentsData, refetch: refetchStudents } = useUsers({
    page: 1,
    searchQuery: debouncedSearchTermStudents || undefined,
    sortBy: 'firstName',
    sortOrder: 'asc',
    role: [SchoolUserRole.STUDENT],
    all: !debouncedSearchTermStudents,
    limit: debouncedSearchTermStudents ? 50 : undefined,
  });

  const { mutateAsync: createClass, isPending } = useCreateClass();

  // Transform users data to include full name
  const studentOptions = useMemo<UserOption[]>(() => {
    return (studentsData?.data || []).map((user) => ({
      id: user.id,
      displayName: `${user.firstName} ${user.lastName || ''}`.trim(),
      email: user.email,
    }));
  }, [studentsData?.data]);

  const teacherOptions = useMemo<UserOption[]>(() => {
    return (teachersData?.data || []).map((user) => ({
      id: user.id,
      displayName: `${user.firstName} ${user.lastName || ''}`.trim(),
      email: user.email,
    }));
  }, [teachersData?.data]);

  const guideOptions = useMemo<UserOption[]>(() => {
    return (guidesData?.data || []).map((user) => ({
      id: user.id,
      displayName: `${user.firstName} ${user.lastName || ''}`.trim(),
      email: user.email,
    }));
  }, [guidesData?.data]);

  const handleSubmit = async (data: {
    className: string;
    students: string[];
    teachers: string[];
    guides: string[];
  }): Promise<void> => {
    if (!data.className.trim()) {
      return;
    }

    try {
      await createClass({
        name: data.className,
        studentIds: data.students || [],
        teacherIds: data.teachers || [],
        guideIds: data.guides || [],
      });
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      refetchStudents();
      refetchTeachers();
      refetchGuides();
      handleClose();
    } catch (error) {
      toast.show({
        title: 'Fehler',
        description:
          'Kurs konnte nicht erstellt werden. Wenn der Fehler weiterhin auftritt, wende dich bitte an den Support.',
        variant: ToastVariant.ERROR,
      });
    }
  };

  // Reset search terms when modal is closed
  useEffect(() => {
    if (!isShown) {
      setSearchTermTeachers('');
      setDebouncedSearchTermTeachers('');
      setSearchTermGuides('');
      setDebouncedSearchTermGuides('');
      setSearchTermStudents('');
      setDebouncedSearchTermStudents('');
    }
  }, [isShown]);

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleClose}
      placement="center"
      hideHeader={false}
      size="md"
    >
      <CustomModalContent
        p={{ base: '0', md: '0 40px 30px' }}
        display="flex"
        flexDirection="column"
      >
        <Flex
          p={{ base: 'l', md: '0' }}
          textAlign={{ base: 'center', md: 'left' }}
          mb={{ md: '30px' }}
          flexDir="column"
          gap="l"
        >
          <Text textStyle="h3.medium" color="text.primary">
            Kurs erstellen
          </Text>
          <Text color="text.tertiary">
            Gib dem Kurs einen Namen und wähle Lehrende und Lernende aus.
          </Text>

          <Form<ClassCreationInput, typeof schema> onSubmit={handleSubmit} schema={schema}>
            {({ control }) => (
              <Flex flexDirection="column" gap="l" alignItems="center" width="100%">
                <RHFFormInput
                  type="text"
                  leftElement={<NovaIcon icon={SchoolIcon} size="m" aria-hidden />}
                  placeholder="Kursname"
                  name="className"
                />
                <Flex gap="xs" direction="column" maxW="100%" width="100%">
                  <HStack gap="0" align="center">
                    <Text textStyle="body.s.medium" color="text.secondary">
                      Lehrende
                    </Text>
                  </HStack>
                  <MultiSelectField
                    name="teachers"
                    control={control}
                    placeholder="Lehrende auswählen"
                    options={teacherOptions}
                    identifierKey="id"
                    labelKey="displayName"
                    secondaryKey="email"
                    searchable
                    searchPlaceholder="Suchen..."
                    width="100%"
                    icon={
                      <NovaIcon icon={EducationIcon} size="m" color="icon.quaternary" aria-hidden />
                    }
                    filterMode="server"
                    onSearchChange={setSearchTermTeachers}
                  />

                  <Text textStyle="body.xs.regular" color="text.secondary" maxW="520px">
                    Erstellen und teilen Materialien, bereiten Arbeitsblätter vor, weisen Aufgaben
                    zu und sehen den Lernfortschritt.
                  </Text>
                </Flex>
                <Flex gap="xs" direction="column" maxW="100%" width="100%">
                  <HStack gap="0" align="center">
                    <Text textStyle="body.s.medium" color="text.secondary">
                      Praxisanleitende
                    </Text>
                  </HStack>
                  <MultiSelectField
                    name="guides"
                    control={control}
                    placeholder="Praxisanleitende auswählen"
                    options={guideOptions}
                    identifierKey="id"
                    labelKey="displayName"
                    secondaryKey="email"
                    searchable
                    searchPlaceholder="Suchen..."
                    width="100%"
                    icon={
                      <NovaIcon icon={EducationIcon} size="m" color="icon.quaternary" aria-hidden />
                    }
                    filterMode="server"
                    onSearchChange={setSearchTermGuides}
                  />

                  <Text textStyle="body.xs.regular" color="text.secondary" maxW="520px">
                    Unterstützen die Lehrenden bei der Durchführung der Praxisarbeiten und begleiten
                    die Lernenden bei ihren praktischen Aufgaben.
                  </Text>
                </Flex>

                <VStack maxW="100%" width="100%">
                  <HStack gap="0" justify="flex-start" width="100%">
                    <Text textStyle="body.s.medium" color="text.secondary">
                      Lernende
                    </Text>
                  </HStack>
                  <MultiSelectField
                    name="students"
                    control={control}
                    placeholder="Lernende auswählen"
                    options={studentOptions}
                    identifierKey="id"
                    labelKey="displayName"
                    secondaryKey="email"
                    searchable
                    searchPlaceholder="Suchen..."
                    width="100%"
                    filterMode="server"
                    onSearchChange={setSearchTermStudents}
                  />
                  <Flex gap="xs" direction="column" width="100%">
                    <Text textStyle="body.xs.regular" color="text.secondary">
                      Lernen mit NOVAHEAL, bearbeiten Aufgaben und verfolgen den eigenen
                      Fortschritt.
                    </Text>
                  </Flex>
                </VStack>

                <Flex gap="10px" width="100%" justifyContent="right">
                  <Button onClick={handleClose} variant="tertiary" w="40%" fontWeight="regular">
                    Abbrechen
                  </Button>
                  <Button type="submit" variant="primary" w="40%" loading={isPending}>
                    Erstellen
                  </Button>
                </Flex>
              </Flex>
            )}
          </Form>
        </Flex>
      </CustomModalContent>
    </CustomModal>
  );
};
